'use client';

import {
  App as AntdApp,
  ConfigProvider,
  theme as antdTheme,
  type ThemeConfig,
} from 'antd';
import { domAnimation, LazyMotion, motion } from 'framer-motion';
import mixpanel from 'mixpanel-browser';
import type { AppProps } from 'next/app';
import { Montserrat } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { RecoilRoot } from 'recoil';

import 'react-toastify/dist/ReactToastify.css';
import '../styles/globals.scss';
import '../styles/square.scss';
import '../styles/toolTip.scss';
import '../styles/ckEditor.scss';
import 'plyr/dist/plyr.css';
import '../styles/youtubeStyle.scss';

import Custom503 from './503';
import Layout from '../components/Layout';
import HydrationSafe from '../components/shared/HydrationSafe';
import CustomLoader from '../components/shared/Loading';
import PageTransition from '../components/shared/PageTransition';
import SmoothScroll from '../components/shared/SmoothScroll';
import { generateBreadcrumbSchema } from '../utils/seoSchemaBuilder';

const montserrat = Montserrat({
  subsets: ['latin'],
  fallback: ['Segoe UI', 'Roboto', 'Arial', 'sans-serif'],
  display: 'swap',
});

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

const config: ThemeConfig = {
  token: { colorPrimary: '#FF5612', fontFamily: 'inherit' },
};

export default function App({ Component, pageProps }: AppProps) {
  const { pathname, route } = useRouter();
  let previousPathname = pathname;
  const environment = process.env.NEXT_PUBLIC_APP_ENV || '';
  const maintenanceMode = process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true';
  const router = useRouter();
  const breadcrumbSchema = generateBreadcrumbSchema(router.asPath);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Handle route change loading states
  useEffect(() => {
    const handleStart = () => {
      setIsLoading(true);
    };

    const handleComplete = () => {
      setIsLoading(false);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
    };
  }, [router]);

  const pagesWithoutLayout = [
    '/pdfReader/[book_id]',
    '/pdfReader/[book_id]/[without_email]',
  ];
  const pagesWithoutRouteAnimation = ['/books/[bookId]'];
  const excludeLayout = pagesWithoutLayout.includes(pathname);
  const excludeRouteAnimation = pagesWithoutRouteAnimation.includes(pathname);

  useEffect(() => {
    if (typeof window !== 'undefined' && MIXPANEL_TOKEN) {
      mixpanel.init(MIXPANEL_TOKEN, { debug: true });
    }
  }, []);

  const trackPageView = (url: string, pageTitle: string) => {
    if (typeof window === 'undefined') return;
    mixpanel.track(pageTitle, {
      url: url,
      pageTitle: pageTitle,
    });
  };

  useEffect(() => {
    if (!MIXPANEL_TOKEN || environment !== 'production') return;

    const handleRouteChange = (url: string) => {
      if (typeof window !== 'undefined' && previousPathname === pathname) {
        const pageTitle = document.title;
        trackPageView(url, pageTitle);
        previousPathname = pathname;
      }
    };

    handleRouteChange(pathname);
  }, [pathname]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (typeof window !== 'undefined') {
        console.clear();
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const TAG_ID = process.env.NEXT_PUBLIC_GOOGLE_ID_TAG;
      (window as any).dataLayer = (window as any).dataLayer || [];

      const gtag = (...args: any[]): void => {
        (window as any).dataLayer.push(...args);
      };

      gtag('js', new Date());
      gtag('config', TAG_ID, { debug_mode: true });
      (window as any).gtag = gtag;
    }
  }, []);

  if (maintenanceMode)
    return (
      <div className={montserrat.className}>
        <ConfigProvider
          theme={{ ...config, algorithm: [antdTheme.darkAlgorithm] }}
        >
          <Custom503 />
        </ConfigProvider>
      </div>
    );

  // Create a simple loading state for server-side rendering
  const loadingContent = (
    <div className='w-full h-screen flex items-center justify-center'>
      <div className='text-center'>
        <div className='text-2xl font-semibold'>
          <CustomLoader />
        </div>
      </div>
    </div>
  );

  // Page transition loading overlay
  const loadingOverlay = isLoading && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center'
    >
      <CustomLoader />
    </motion.div>
  );

  const content = <Component {...pageProps} />;

  const robotsContent =
    process.env.NEXT_PUBLIC_APP_ENV !== 'production'
      ? 'noindex, nofollow'
      : 'index, follow';
  const canonicalUrl = `https://zebralearn.com${router.asPath.split('?')[0]}`;

  return (
    <>
      <Head>
        <meta
          name='keywords'
          content='Bestselling Books, Courses, Learning, School, ecommerce, Affiliate, marketing, Online learning, E-books, Education, Learning platform, Study materials, Digital library, Online courses, Educational resources, Self-paced learning, Interactive learning, Distance learning, E-learning, Skill development, Knowledge acquisition, Study guides, Learning materials, Academic resources, Online education, Study resources, Digital textbooks, Tutorial videos, Exam preparation, Test materials, Study aids, Reference materials, Audio books, Video lectures, Educational content, Learning modules, Study notes, Webinars, Interactive courses, Study platform, Certification courses, Online training, Skill enhancement, Academic courses, Professional development, Career advancement, Learning management system, Virtual classroom, Online workshops, Exam practice, Study skills, Academic support, Educational technology, Lifelong learning, Study habits, Course materials, Online tutorials, Learning community, Study groups, Practice tests, Online degrees, Course catalogs, Study strategies, Learning resources, Educational tools, Online seminars, Online study groups, Homework help, Study techniques, Digital learning, Educational courses, Online classrooms, Learning environment, Study assistance, Exam tips, Study motivation, Skill-based courses, Career skills, Online textbooks, Study plans, Online coaching, Study sessions, Study aids, Online quizzes, Academic success, Online support, Study schedule, Learning objectives, Exam readiness, Academic achievement, Learning outcomes, Course completion, Test preparation '
        />
        <meta name='robots' content={robotsContent} key='robotsId' />
        <meta name='googlebot' content={robotsContent} />
        <meta charSet='utf-8' />

        <meta name='viewport' content='width=device-width, initial-scale=1' />

        {/* Canonical URL */}
        <link
          rel='canonical'
          href={canonicalUrl || 'https://zebralearn.com'}
          key='canonical'
        />

        {/* Open Graph (Default) */}
        <meta property='og:site_name' content='ZebraLearn' key='og-site_name' />
        <meta property='og:title' content='ZebraLearn' key='og-title' />
        <meta
          property='og:description'
          content='Discover the best books and courses on finance, career growth, and more.'
          key='og-description'
        />
        <meta property='og:type' content='website' key='og-type' />
        <meta property='og:url' content='https://zebralearn.com' key='og-url' />
        <meta
          property='og:image'
          content='https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG%2B1.png?tr=f-png&v=2'
          key='og-image'
        />
        <meta property='og:locale' content='en_IN' key='og-locale' />
        <meta
          property='og:image:type'
          content='image/webp'
          key='og-image-type-webp'
        />
        <meta
          property='og:image:type'
          content='image/png'
          key='og-image-type-png'
        />
        <meta property='og:image:width' content='1200' key='ogimagewidth' />
        <meta property='og:image:height' content='630' key='ogimageheight' />
        <meta
          name='google-site-verification'
          content={process.env.NEXT_PUBLIC_SITE_VERIFICATION}
        />

        {/* Twitter Cards (Default) */}
        <meta
          name='twitter:card'
          content='summary_large_image'
          key='twitter-card'
        />
        <meta name='twitter:site' content='@zebralearn' key='twitter-site' />
        <meta
          name='twitter:creator'
          content='@zebralearn'
          key='twitter-creator'
        />
        <meta name='twitter:title' content='ZebraLearn' key='twitter-title' />
        <meta
          name='twitter:description'
          content='Find expert-reviewed books and courses tailored to your learning needs.'
          key='twitter-description'
        />
        <meta
          name='twitter:image'
          content='https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG+1.png?tr=f-png'
          key='twitter-image'
        />

        {/* Other Metadata */}
        <meta property='og:image:alt' content='ZebraLearn' key='og-image-alt' />
        <meta
          property='og:image:type'
          content='image/png'
          key='og-image-type'
        />
        <meta property='og:image:width' content='1200' key='og-image-width' />
        <meta property='og:image:height' content='630' key='og-image-height' />
      </Head>

      <Script
        id='breadcrumb-schema'
        type='application/ld+json'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className={montserrat.className}>
        {!isHydrated ? (
          // Show a simple loading state during server-side rendering and initial client render
          loadingContent
        ) : (
          // Only render the full app after hydration is complete
          <SmoothScroll>
            <RecoilRoot>
              <ConfigProvider
                theme={{ ...config, algorithm: [antdTheme.darkAlgorithm] }}
              >
                <AntdApp>
                  <HydrationSafe>
                    <LazyMotion features={domAnimation}>
                      {loadingOverlay}
                      {excludeLayout ? (
                        <PageTransition route={route}>{content}</PageTransition>
                      ) : (
                        <Layout>
                          {excludeRouteAnimation ? (
                            content
                          ) : (
                            <PageTransition route={route}>
                              {content}
                            </PageTransition>
                          )}
                        </Layout>
                      )}
                    </LazyMotion>
                  </HydrationSafe>
                </AntdApp>
              </ConfigProvider>
              <ToastContainer autoClose={2000} theme='dark' />
            </RecoilRoot>
          </SmoothScroll>
        )}
      </div>
    </>
  );
}
